import { type FC, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

import { ActionIcon, Button, CloseButton, Drawer, Popover } from "@mantine/core";
import { RefreshIcon } from "@snapchat/snap-design-system-icons";
import { FormattedMessage, useIntl } from "react-intl";

import snapchatGhostSvg from "@app-assets/svg/snapchat.svg";

import { useAssistantChat, useAssistantChatOpen } from "../hooks";
import { getPageName } from "../utils";

import styles from "./AssistantChatDrawer.module.css";
import { AssistantChatInput } from "./AssistantChatInput";
import { AssistantChatMessageList } from "./AssistantChatMessageList";
import { messages } from "./messages.generated";

const ACTION_ICON_SIZE = 16;

export const AssistantChatDrawer: FC = () => {
    const intl = useIntl();
    const { isOpen, close } = useAssistantChatOpen();
    const { messages: chatMessages, isSending, hasError, send, clear } = useAssistantChat();
    const { pathname } = useLocation();
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [resetToken, setResetToken] = useState(0);

    // Optimistic append means a live thread always has >=1 message; checking
    // length alone hides the control while a stale request settles after Start over.
    const hasThread = chatMessages.length > 0;

    // A confirm popover left open when the request finishes on its own would
    // otherwise reopen the instant the next message starts sending.
    useEffect(() => {
        if (!isSending) setConfirmOpen(false);
    }, [isSending]);

    const startOver = (): void => {
        clear();
        setConfirmOpen(false);
        // Remount the composer so its internal draft resets to empty.
        setResetToken((token) => token + 1);
    };

    const startOverButton = (
        <ActionIcon
            variant="subtle"
            radius="xl"
            className={styles.headerButton}
            onClick={
                isSending
                    ? () => {
                          setConfirmOpen(true);
                      }
                    : startOver
            }
            aria-label={intl.formatMessage(messages.startOverAriaLabel)}
        >
            <RefreshIcon size={ACTION_ICON_SIZE} />
        </ActionIcon>
    );

    return (
        <Drawer
            opened={isOpen}
            onClose={close}
            position="right"
            size="md"
            withCloseButton={false}
            padding={0}
            classNames={{ body: styles.body, header: styles.drawerHeader }}
            closeOnClickOutside={false}
            trapFocus
            title={intl.formatMessage(messages.title)}
        >
            <div className={styles.header}>
                <span className={styles.iconBadge}>
                    <img className={styles.headerIcon} src={snapchatGhostSvg} alt="" />
                </span>
                <div className={styles.headerText}>
                    <h2 className={styles.title}>
                        <FormattedMessage {...messages.title} />
                    </h2>
                    <span className={styles.contextChip}>
                        <span className={styles.contextDot} aria-hidden="true" />
                        <FormattedMessage {...messages.contextChip} values={{ page: getPageName(pathname) }} />
                    </span>
                </div>
                <div className={styles.headerActions}>
                    {hasThread &&
                        (isSending ? (
                            <Popover
                                opened={confirmOpen}
                                onChange={setConfirmOpen}
                                position="bottom-end"
                                radius={16}
                                shadow="md"
                                withArrow
                                trapFocus
                                transitionProps={{ duration: 0 }}
                            >
                                <Popover.Target>{startOverButton}</Popover.Target>
                                <Popover.Dropdown className={styles.confirmDropdown}>
                                    <p className={styles.confirmTitle}>
                                        <FormattedMessage {...messages.confirmRestartTitle} />
                                    </p>
                                    <div className={styles.confirmActions}>
                                        <Button
                                            variant="subtle"
                                            color="gray"
                                            radius="xl"
                                            size="xs"
                                            onClick={() => {
                                                setConfirmOpen(false);
                                            }}
                                        >
                                            <FormattedMessage {...messages.confirmRestartCancel} />
                                        </Button>
                                        <Button
                                            radius="xl"
                                            size="xs"
                                            className={styles.confirmConfirm}
                                            onClick={startOver}
                                        >
                                            <FormattedMessage {...messages.confirmRestartConfirm} />
                                        </Button>
                                    </div>
                                </Popover.Dropdown>
                            </Popover>
                        ) : (
                            startOverButton
                        ))}
                    <CloseButton
                        className={styles.headerButton}
                        radius="xl"
                        onClick={close}
                        aria-label={intl.formatMessage(messages.closeAriaLabel)}
                    />
                </div>
            </div>
            <div className={styles.content}>
                <AssistantChatMessageList
                    messages={chatMessages}
                    isSending={isSending}
                    hasError={hasError}
                    onSuggestionSelect={(prompt) => {
                        void send(prompt);
                    }}
                />
                <div className={styles.footer}>
                    <AssistantChatInput
                        key={resetToken}
                        disabled={isSending}
                        onSend={(question) => {
                            void send(question);
                        }}
                    />
                    <p className={styles.ephemeralNote}>
                        <FormattedMessage {...messages.ephemeralNote} />
                    </p>
                    <p className={styles.disclaimer}>
                        <FormattedMessage {...messages.genAiDisclaimer} />
                    </p>
                </div>
            </div>
        </Drawer>
    );
};
